import {
    Address,
    ApplicantEmployment,
    Citizenship,
    EmploymentStatus,
    LoanApplicant,
    LoanApplicantIdDocument,
    PropertyType,
    ResidenceOwnership2,
    ResidenceType,
    SpokenLanguage,
    SubmitPrequalRequest,
} from '@servicetitan/financing-api-client';
import { FIRST_LOOK_RESIDENCE_OWNERSHIP } from '@servicetitan/financing-workflow-shared';
import { ApplicationData } from '../application/ApplicationDataStore';
import { calculateIncomes } from '../../utils/calculateIncomes';
import { sanitizeFieldValue } from '../../utils/sanitizeFieldValue';
import { prepareCoApplicantSpouseData } from '../spouseInfo';

export interface PrepareSubmitPrequalRequestParams {
    applicationData: ApplicationData;
    providerName: string;
    tenantId: string;
    buId: string;
    requestedAmount: number;
    planNumbers?: string[];
    hasMailingAddressStep?: boolean;
    prequalLinkId?: string | null;
    iovationBlackbox?: string | null;
    technicianLastName?: string | null;
    technicianPhone?: string | null;
    technicianEmail?: string | null;
}

const sanitizeSSN = (ssn: string | undefined): string | undefined => {
    if (!ssn) {
        return undefined;
    }
    return ssn.replace(/-/g, '');
};

const sanitizePhone = (phone: string | undefined): string | undefined => {
    if (!phone) {
        return undefined;
    }
    const digits = phone.replace(/\D/g, '');
    return digits.length > 0 ? digits : undefined;
};

const sanitizeHousingType = (residenceType: unknown): ResidenceOwnership2 =>
    (sanitizeFieldValue(residenceType, FIRST_LOOK_RESIDENCE_OWNERSHIP) ??
        ResidenceOwnership2.NotSet) as ResidenceOwnership2;

export const prepareSubmitPrequalRequest = ({
    applicationData,
    providerName,
    tenantId,
    buId,
    requestedAmount,
    planNumbers,
    hasMailingAddressStep,
    prequalLinkId,
    iovationBlackbox,
    technicianLastName,
    technicianPhone,
    technicianEmail,
}: PrepareSubmitPrequalRequestParams): SubmitPrequalRequest => {
    const primaryApplicantData = applicationData.applicant.primaryApplicant;
    const primaryJobDetails = primaryApplicantData?.jobDetails;
    const jobAddress = primaryJobDetails?.address;

    const homeAddress = hasMailingAddressStep
        ? applicationData.mailingAddress.primaryApplicant?.address
        : primaryJobDetails?.mailingAddressDiffersFromJobAddress
          ? primaryJobDetails?.mailingAddress?.address
          : jobAddress;

    const { grossMonthlyIncome = 0 } = calculateIncomes({
        grossMonthlyIncome: primaryApplicantData?.employment?.grossMonthlyIncome,
        annualIncome: primaryApplicantData?.annualIncome,
    });

    const coApplicantSection = applicationData.coApplicant;
    const coApplicantData =
        coApplicantSection?.hasCoApplicant === true && 'coApplicant' in coApplicantSection
            ? coApplicantSection.coApplicant
            : undefined;

    let coApplicant: LoanApplicant | undefined;
    if (coApplicantData) {
        const {
            grossMonthlyIncome: coApplicantGrossMonthlyIncome = 0,
            annualIncome: coApplicantAnnualIncome = 0,
        } = calculateIncomes({
            grossMonthlyIncome: coApplicantData.employment?.grossMonthlyIncome,
            annualIncome: coApplicantData.annualIncome,
        });

        const coApplicantAddress =
            coApplicantData.isAddressSameAsPrimary === true ? homeAddress : coApplicantData.address;

        coApplicant = {
            firstName: coApplicantData.firstName,
            middleInitial: coApplicantData.middleInitial,
            lastName: coApplicantData.lastName,
            dateOfBirth: coApplicantData.dateOfBirth ?? '',
            ssn: sanitizeSSN(coApplicantData.ssn),
            emailAddress: coApplicantData.email,
            primaryPhone: sanitizePhone(coApplicantData.primaryPhone),
            alternatePhone: sanitizePhone(coApplicantData.alternatePhone),
            workPhone: sanitizePhone(coApplicantData.workPhone),
            housingType: sanitizeHousingType(coApplicantData.jobDetails?.residenceType),
            monthlyHousingPayment: 0,
            propertyType: PropertyType.NotSet,
            address: (coApplicantAddress as Address) ?? null,
            applicantId: coApplicantData.applicantId as LoanApplicantIdDocument | undefined,
            employment: {
                grossMonthlyIncome: coApplicantGrossMonthlyIncome,
                employerName: coApplicantData.employment?.employerName,
                employerPhone: sanitizePhone(coApplicantData.employment?.employerPhone),
            } as ApplicantEmployment,
            annualIncome: coApplicantAnnualIncome,
            residenceType: (coApplicantData.typeOfHome as ResidenceType) ?? ResidenceType.NotSet,
            employmentStatus:
                (coApplicantData.employment?.status as EmploymentStatus) ?? EmploymentStatus.NotSet,
            citizenship: (coApplicantData.citizenship as Citizenship) ?? Citizenship.NotSet,
            spokenLanguage:
                (coApplicantData.spokenLanguage as SpokenLanguage) ?? SpokenLanguage.NotSet,
            signature: coApplicantData.signature,
            ...prepareCoApplicantSpouseData(
                applicationData,
                coApplicantData.jobDetails,
                coApplicantAddress,
            ),
        };
    }

    return {
        providerName,
        tenantId,
        buId,
        prequalLinkId: prequalLinkId ?? null,
        firstName: primaryApplicantData?.firstName ?? null,
        middleInitial: primaryApplicantData?.middleInitial ?? null,
        lastName: primaryApplicantData?.lastName ?? null,
        dateOfBirth: primaryApplicantData?.dateOfBirth ?? '',
        ssn: sanitizeSSN(primaryApplicantData?.ssn) ?? null,
        emailAddress: primaryApplicantData?.email ?? null,
        primaryPhone: sanitizePhone(primaryApplicantData?.primaryPhone) ?? null,
        iovationBlackbox: iovationBlackbox ?? null,
        technicianLastName: technicianLastName ?? null,
        technicianPhone: sanitizePhone(technicianPhone ?? undefined) ?? null,
        technicianEmail: technicianEmail ?? null,
        address: (homeAddress as Address) ?? null,
        grossMonthlyIncome,
        requestedAmount,
        planNumbers: planNumbers ?? null,
        coApplicant: coApplicant ?? null,
    };
};
